// src/pages/HomePage.js
// Clean home screen: short banner, the worker's name, any alerts that concern
// them, then three large touch targets. No company-wide stat tiles — those live
// on the Admin dashboard, and a worker in the field does not act on them.
//
// Tools are not given automatic deadlines, so there is no overdue banner here.
// If that is ever wanted it belongs behind an admin option.
//
// The previous, denser home screen is still available at /dashboard-legacy.
import React, { useContext, useEffect, useState } from 'react';
import MainMenuBar from '../components/MainMenuBar';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import PendingBanner from '../components/PendingBanner';
import { canTransferItems } from '../permissions';
import {
  getMyPendingTransfers,
  getMyToolRequests,
  getWorkerInventory,
} from '../services/api';

// Decorative QR-style glyph for the Scan button. Not a real, scannable code —
// it is a visual cue that this button opens the camera. Drawn inline so there is
// no image asset to load and it stays crisp at any size.
//
// The three finder squares are drawn as rings (four bars plus a centre block)
// rather than a filled square with a background-coloured inset, so the glyph
// works on any button colour. The data modules come from a fixed arithmetic
// pattern, so the glyph looks irregular but never changes between renders.
const QR_MODULES = 25;

function QrGlyph({ size = 72 }) {
  const inFinderZone = (x, y) =>
    (x < 8 && y < 8) ||
    (x > QR_MODULES - 9 && y < 8) ||
    (x < 8 && y > QR_MODULES - 9);

  const modules = [];
  for (let y = 0; y < QR_MODULES; y++) {
    for (let x = 0; x < QR_MODULES; x++) {
      if (inFinderZone(x, y)) continue;
      if ((x * 5 + y * 3 + ((x * y) % 7)) % 3 === 0) {
        modules.push(<rect key={`m${x}-${y}`} x={x} y={y} width="1" height="1" />);
      }
    }
  }

  const finder = (ox, oy) => (
    <g key={`f${ox}-${oy}`}>
      <rect x={ox}     y={oy}     width="7" height="1" />
      <rect x={ox}     y={oy + 6} width="7" height="1" />
      <rect x={ox}     y={oy + 1} width="1" height="5" />
      <rect x={ox + 6} y={oy + 1} width="1" height="5" />
      <rect x={ox + 2} y={oy + 2} width="3" height="3" />
    </g>
  );

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${QR_MODULES} ${QR_MODULES}`}
      fill="currentColor"
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      {finder(0, 0)}
      {finder(QR_MODULES - 7, 0)}
      {finder(0, QR_MODULES - 7)}
      {modules}
    </svg>
  );
}

export default function HomePage() {
  const { worker } = useContext(AuthContext);
  const navigate = useNavigate();
  const [transfers, setTransfers]       = useState([]);
  const [toolRequests, setToolRequests] = useState([]);
  const [myItems, setMyItems]           = useState([]);
  const [itemsLoaded, setItemsLoaded]   = useState(false);

  useEffect(() => {
    getMyPendingTransfers()
      .then(r => setTransfers(r.data.transfers || []))
      .catch(() => {});
    getMyToolRequests()
      .then(r => setToolRequests(r.data.requests || []))
      .catch(() => {});
    if (worker?.worker_id) {
      getWorkerInventory(worker.worker_id)
        .then(r => setMyItems(r.data.items || []))
        .catch(() => {})
        .finally(() => setItemsLoaded(true));
    } else {
      setItemsLoaded(true);
    }
  }, [worker]);

  const firstName = worker?.first_name
    || (worker?.display_name || '').split(' ')[0]
    || 'there';

  return (
    <div className="screen">
      <div className="screen-header" style={{
        flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 3, textAlign: 'center',
      }}>
        <h2 style={{
          color: 'white', fontSize: '1.45rem', fontWeight: 800,
          letterSpacing: '0.05em', margin: 0, lineHeight: 1.15,
        }}>
          NGC ASSET TRACKER
        </h2>
        <p style={{
          color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem',
          fontWeight: 500, margin: 0,
        }}>
          User: {worker?.display_name || firstName}
        </p>
      </div>

      <div className="screen-content" style={{ display: 'flex', flexDirection: 'column', paddingTop: 16 }}>

        <PendingBanner
          toolRequests={toolRequests}
          transfers={transfers}
          onRequestDeclined={id =>
            setToolRequests(prev => prev.filter(r => r.request_id !== id))
          }
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <button
            className="btn btn-primary"
            style={{
              minHeight: 190, borderRadius: 18, fontWeight: 700,
              flexDirection: 'column', gap: 14, padding: '20px',
            }}
            onClick={() => navigate('/scan')}
          >
            <QrGlyph size={84} />
            <span style={{ fontSize: '1.4rem' }}>Scan QR Code</span>
          </button>

          {/* Scan Multiple: several tools to a person + job, or onto a truck (10/29) */}
          {canTransferItems(worker?.role) && (
            <button
              className="btn home-tile-btn"
              style={{ minHeight: 88, fontSize: '1.2rem', borderRadius: 18, fontWeight: 700 }}
              onClick={() => navigate('/scan-multiple')}
            >
              <span className="home-tile-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7V5a1 1 0 011-1h2M17 4h2a1 1 0 011 1v2M20 17v2a1 1 0 01-1 1h-2M7 20H5a1 1 0 01-1-1v-2M8 9h2v2H8zM14 9h2v2h-2zM8 13h2v2H8zM14 13h2v2h-2z" /></svg></span>
              Scan Multiple
            </button>
          )}

          <button
            className="btn home-tile-btn"
            style={{ minHeight: 88, fontSize: '1.2rem', borderRadius: 18, fontWeight: 700 }}
            onClick={() => navigate('/my-items')}
          >
            <span className="home-tile-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg></span>
            My Items{itemsLoaded ? ` (${myItems.length})` : ''}
          </button>

          <button
            className="btn home-tile-btn"
            style={{ minHeight: 88, fontSize: '1.2rem', borderRadius: 18, fontWeight: 700 }}
            onClick={() => navigate('/search')}
          >
            <span className="home-tile-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg></span>
            Search
          </button>
        </div>

        <p style={{
          textAlign: 'center', color: 'var(--text-muted)',
          fontSize: '0.8rem', marginTop: 18,
        }}>
          Scan a tool's QR code to check in, check out, or transfer it
        </p>
        <MainMenuBar />
      </div>
    </div>
  );
}
